import React, { useEffect, useState } from 'react';
import { Anchor, Button, PageHeader } from '@keplerco/core';
import { FieldValues, useForm } from 'react-hook-form';
import classNames from 'classnames';
import layoutStyles from '../../../../design/layout.module.css';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import assessmentsWizardStyles from '../../assessments-wizard.module.css';
import { useKeplerNavigate } from '../../../../navigation/guards/use-kepler-navigate';
import { AssessmentSubType, AssessmentType } from '../../../../enums/assessment-type';
import { useAppActions, useAppState } from '../../../../overmind';
import { AssessmentRequest, AssessmentResponse } from '../../../../overmind/actions';
import { AssessmentStatus } from '../../../../enums/assessment-status';
import { PagePath } from '../../../../navigation/navigation.enums';
import { useParams } from 'react-router';
import { DetailsWidget } from '../details.widget';
import { ScheduleWidget } from '../schedule.widget';

export function EditActiveRatingAssessment(): JSX.Element {
  const keplerNavigate = useKeplerNavigate();
  const { assessmentSlug } = useParams();

  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const { control, handleSubmit, watch, reset } = useForm({ mode: 'onBlur' });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [assessment, setAssessment] = useState<AssessmentResponse>();

  const startDate = watch('startDate');
  const endDate = watch('endDate');

  async function getData(assessmentSlug: string) {
    const assessmentResponse = await actions.getAssessment(assessmentSlug);
    setAssessment(assessmentResponse);
    reset({ name: assessmentResponse?.name, description: assessmentResponse?.description, startDate: assessmentResponse?.startDate, endDate: assessmentResponse?.endDate });
  }

  useEffect(() => {
    async function initialise() {
      if (!assessmentSlug) return;

      setIsLoading(true);
      await getData(assessmentSlug);
      setIsLoading(false);
    }

    initialise();
  }, [assessmentSlug, companyVariables.slug]);

  async function saveDetails(fieldValues: FieldValues) {
    if (!assessmentSlug) return;

    setIsLoading(true);

    const request: AssessmentRequest = {
      assessmentType: AssessmentType.PeerEndorsement,
      assessmentStatus: AssessmentStatus.Active,
      companySlug: companyVariables.slug!,
      slug: assessmentSlug,
      name: fieldValues?.name ?? '',
      description: fieldValues?.description ?? '',
      startDate: fieldValues?.startDate,
      endDate: fieldValues?.endDate,
      allowReassessment: false,
      forceAssessment: false,
      assessmentSubType: AssessmentSubType.RatingAssessment
    };

    await actions.updateAssessment(request);
    await getData(assessmentSlug);

    keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementViewRatingAssessment.replace(':companySlug', companyVariables.slug!).replace(':assessmentSlug', assessmentSlug!)}`);

    setIsLoading(false);
  }

  return (
    <div className="pageWrapper wrapper" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <PageHeader
        breadcrumbs={[
          {
            name: 'Assessment Management',
            url: `${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}`,
          },
          {
            name: assessment?.name || 'Rating assessment',
            url: `${PagePath.assessmentManagementBase}${PagePath.assessmentManagementViewRatingAssessment.replace(':companySlug', companyVariables.slug!).replace(':assessmentSlug', assessmentSlug!)}`,
          },
          {
            name: 'Edit assessment',
            url: location.pathname,
          },
        ]}
        title="Edit assessment"
      />

      <form id="editAssessment" className={assessmentsWizardStyles.stepContainer} onSubmit={handleSubmit(saveDetails)}>
        <SkeletonOverlay isLoading={isLoading}>
          <DetailsWidget control={control} />
        </SkeletonOverlay>

        <SkeletonOverlay isLoading={isLoading}>
          <ScheduleWidget control={control} startDate={startDate} endDate={endDate} subtitle="Select end date for this assessment" />
        </SkeletonOverlay>

        <footer className={classNames('card', layoutStyles.wizardFooter)}>
          <Anchor reverse arrow aria-label="Return to previous page" onClick={() => keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagementViewRatingAssessment.replace(':companySlug', companyVariables.slug!).replace(':assessmentSlug', assessmentSlug!)}`)}>
            Cancel
          </Anchor>

          <Button filled type="button" arrow aria-label="Save and return to previous page">
            Save
          </Button>
        </footer>
      </form>
    </div>
  );
}
